import * as React from 'react'
import { Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Paper, TableSortLabel } from '@mui/material'
import { Check as CheckIcon } from '@mui/icons-material'
import { styled } from '@mui/system'
import { formatCurrency } from 'utils/formatters'

// Data row styling
const DataRow = styled(TableRow)(({ theme }) => ({
  [`& th:first-of-type`]: {
    position: 'sticky',
    left: 0,
    minWidth: '200px',
  },
  [`thead & th:first-of-type`]: {
    zIndex: 3,
  },
  [`thead & th`]: {
    textWrap: 'nowrap',
  },
}))

// Data columns array
const columns = [
  {
    id: 'customerName',
    label: 'Customer',
    numeric: false,
    sortable: true,
    summable: false,
  },
  {
    id: 'orderNo',
    label: 'Order No.',
    numeric: true,
    sortable: true,
    summable: false,
  },
  {
    id: 'mealsNo',
    label: '# Meals',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'wrapsNo',
    label: '# Wraps',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'discount',
    label: 'Discount',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'subTotal',
    label: 'Subtotal',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'deliveryCharges',
    label: 'Delivery',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'gst',
    label: 'GST',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'qst',
    label: 'QST',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'total',
    label: 'Total',
    numeric: true,
    sortable: true,
    summable: true,
  },
  {
    id: 'isPaid',
    label: 'Paid',
    numeric: true,
    sortable: true,
    summable: false,
  },
]

/**
 * OrderList component for displaying orders into table
 */
const OrderList = ({ orders }) => {
  const [sort, setSort] = React.useState({ column: 'type', order: 'desc' })

  // Sort function
  const sortBy = (column) => {
    column = column === 'customer' ? 'type' : column
    setSort({
      column: column,
      order: sort.column === column && sort.order === 'asc' ? 'desc' : 'asc',
    })
  }

  // Sorting data
  orders.sort((a, b) => {
    if (a[sort.column] < b[sort.column]) return sort.order === 'asc' ? -1 : 1
    if (a[sort.column] > b[sort.column]) return sort.order === 'asc' ? 1 : -1
    return 0
  })

  // Calculating totals
  const totals = columns.reduce((result, column) => {
    if (column.summable) result[column.id] = Math.round(orders.reduce((total, order) => total + order[column.id], 0) * 100) / 100
    return result
  }, {})

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 6, overflowX: 'auto' }}>
      <Table stickyHeader>
        <TableHead>
          <DataRow>
            {columns.map((column) => (
              <TableCell key={column.id} align={column.numeric ? 'right' : 'left'}>
                <TableSortLabel
                  active={sort.column === column.id}
                  direction={sort.column === column.id ? sort.order : 'asc'}
                  onClick={() => sortBy(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </DataRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <DataRow key={order._id} hover>
              <TableCell component="th" sx={{ bgcolor: order.type }}>
                {order.customerName}
              </TableCell>
              <TableCell align="right">{order.orderNo}</TableCell>
              <TableCell align="right">{order.mealsNo}</TableCell>
              <TableCell align="right">{order.wrapsNo}</TableCell>
              <TableCell align="right" sx={{ color: 'warning.main' }}>
                {formatCurrency(order.discount, { fromCents: true })}
              </TableCell>
              <TableCell align="right">{formatCurrency(order.subTotal, { fromCents: true })}</TableCell>
              <TableCell align="right">{order.deliveryCharges > 0 ? formatCurrency(order.deliveryCharges, { fromCents: true }) : '-'}</TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>
                {formatCurrency(order.gst, { fromCents: true })}
              </TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>
                {formatCurrency(order.qst, { fromCents: true })}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'fontWeightBold', color: 'success.main' }}>
                {formatCurrency(order.total, { fromCents: true })}
              </TableCell>
              <TableCell align="right" sx={{ py: 0 }}>
                {order.isPaid == true && <CheckIcon />}
              </TableCell>
            </DataRow>
          ))}
        </TableBody>
        <TableHead>
          <DataRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: 'grey.200' }}>
            <TableCell>Total</TableCell>
            <TableCell align="right"></TableCell>
            <TableCell align="right">{totals.mealsNo}</TableCell>
            <TableCell align="right">{totals.wrapsNo}</TableCell>
            <TableCell align="right" sx={{ color: 'warning.main' }}>
              {formatCurrency(totals.discount, { fromCents: true })}
            </TableCell>
            <TableCell align="right">{formatCurrency(totals.subTotal, { fromCents: true })}</TableCell>
            <TableCell align="right">{formatCurrency(totals.deliveryCharges, { fromCents: true })}</TableCell>
            <TableCell align="right" sx={{ color: 'error.main' }}>
              {formatCurrency(totals.gst, { fromCents: true })}
            </TableCell>
            <TableCell align="right" sx={{ color: 'error.main' }}>
              {formatCurrency(totals.qst, { fromCents: true })}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'fontWeightBold', color: 'success.main' }}>
              {formatCurrency(totals.total, { fromCents: true })}
            </TableCell>
            <TableCell align="right"></TableCell>
          </DataRow>
        </TableHead>
      </Table>
    </TableContainer>
  )
}

export default OrderList
